import { meetToken } from '@/auth/session'
import { config } from '@/config'

export type RoomMedia = 'audio' | 'video'

export interface RoomView {
  room_id: string
  code: string
  org_id: string
  title: string | null
  media: RoomMedia
  created_by: string
  created_by_name: string
  source: { app: string; ref: string } | null
  lobby: boolean
  /** Nhận khách ngoài (không có tài khoản) xin vào. */
  guests: boolean
  status: 'open' | 'live' | 'ended'
  created_at: string
  started_at: string | null
  ended_at: string | null
  duration_seconds: number | null
  peak: number
  parent_room_id: string | null
  description: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  participants: Array<{ userId: string; name: string }>
}

export interface NewRoom {
  title?: string
  media: RoomMedia
  lobby?: boolean
  guests?: boolean
  description?: string
  schedule?: { start: string; end: string }
}

export class MeetApiError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { token } = await meetToken()
  const headers: Record<string, string> = { authorization: `Bearer ${token}` }
  if (body !== undefined) headers['content-type'] = 'application/json'
  const res = await fetch(`${config.meetApi}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; message?: string } & T
  if (!res.ok || !data.ok) throw new MeetApiError(res.status, data.error ?? 'error', data.message ?? 'Có lỗi, vui lòng thử lại.')
  return data
}

/** Mã thiết bị / trình duyệt này (tự sinh, lưu ở máy): bấm Nghe lại trên chính máy này không bị coi là máy khác. */
export function deviceId(): string {
  const key = 'meetubot.device'
  try {
    const known = localStorage.getItem(key)
    if (known && /^[A-Za-z0-9_-]{8,64}$/.test(known)) return known
    const fresh = crypto.randomUUID().replace(/-/g, '')
    localStorage.setItem(key, fresh)
    return fresh
  } catch {
    return crypto.randomUUID().replace(/-/g, '')
  }
}

/** Gọi không cần đăng nhập (đường dẫn khách). */
async function publicCall<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${config.meetApi}${path}`, {
    method,
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; message?: string } & T
  if (!res.ok || !data.ok) throw new MeetApiError(res.status, data.error ?? 'error', data.message ?? 'Có lỗi, vui lòng thử lại.')
  return data
}

export interface GuestRoomInfo {
  code: string
  title: string | null
  media: RoomMedia
  created_by_name: string
  scheduled_start: string | null
  scheduled_end: string | null
}

export const guestApi = {
  /** Phòng có nhận khách ngoài không (phòng không có cũng trả `allowed: false`). */
  info: (code: string) => publicCall<{ allowed: boolean; room?: GuestRoomInfo }>('GET', `/rooms/${encodeURIComponent(code)}/guest`),
  /** Nhập tên xin vào: nhận token khách của phòng này. */
  enter: (code: string, name: string, note: string) =>
    publicCall<{ token: string; user_id: string; name: string; expires_at: string }>('POST', `/rooms/${encodeURIComponent(code)}/guest`, {
      name,
      ...(note.trim() ? { note } : {}),
    }),
}

export const meetApi = {
  createRoom: (input: NewRoom) =>
    call<{ room: RoomView; created: boolean }>('POST', '/rooms', input).then((r) => r.room),
  room: (key: string) => call<{ room: RoomView }>('GET', `/rooms/${encodeURIComponent(key)}`).then((r) => r.room),
  recent: () => call<{ items: RoomView[] }>('GET', '/rooms?limit=20').then((r) => r.items),
  upcoming: () => call<{ items: RoomView[] }>('GET', '/rooms/upcoming').then((r) => r.items),
  end: (key: string) => call<object>('POST', `/rooms/${encodeURIComponent(key)}/end`),
  decline: (key: string) => call<object>('POST', `/rooms/${encodeURIComponent(key)}/decline`),
  /** Nghe máy, trước khi vào phòng. 409: đã nghe ở máy khác / đã từ chối / đã kết thúc; 410: hết giờ đổ chuông. */
  acceptCall: (callId: string) => call<object>('POST', `/calls/${encodeURIComponent(callId)}/accept`, { device_id: deviceId() }),
  declineCall: (callId: string) => call<object>('POST', `/calls/${encodeURIComponent(callId)}/decline`),
}
